<template>
  <ModalComponent :activated="activated" modal-style="width: 100%" body-style="padding: 0" @close="emit('close')">
    <template #header>
      <slot name="header" />
    </template>

    <div class="layout-container">
      <div id="select-countries">
        <div class="position-sticky bg-white" style="top: 0">
          <div class="header">
            <input v-model="toggleAllCountriesModel" :indeterminate="selectedCountries.length > 0 && !allCountriesSelected" type="checkbox" />
            Välj länder ({{ selectedCountries.length }} av {{ store.availableCountries.length }} valda)
          </div>
        </div>
        <div class="countries content">
          <div v-for="(option, i) of availableCountriesOptions" :key="i" class="value">
            <input :id="`sale-country-${i}`" v-model="selectedCountries" type="checkbox" :value="option.value" />
            <label :for="`sale-country-${i}`">
              {{ option.displayValue }}
              <img :src="getCountryFlag(option.value)" class="tiny-flag" />
            </label>
          </div>
        </div>
      </div>

      <div id="select-skus">
        <div class="position-sticky bg-white" style="top: 0">
          <div class="header">
            <input v-model="toggleAllSkusModel" :indeterminate="toggleAllIndeterminate" type="checkbox" />
            Välj artiklar i urvalet ({{ selectedSkus.size }} av {{ querySkus?.total }} valda)
          </div>
        </div>

        <div class="products minimalistic content">
          <div v-for="(product, i) of paginatedProducts?.data" :key="i" class="value">
            <input :id="`sale-product-${i}`" v-model="selectedSkus" type="checkbox" :value="product.sku" />
            <label :for="`sale-product-${i}`">
              <MoreReadableSkuComponent :sku="product.sku" :is-temporary-sku="product.hasTemporarySku" /> -

              <DisplayValueComponent :primary-value="getMainName(product)" :fallback="getAltLanguageField(product, 'name')?.value">
                <template #default="{ primaryValue, fallback }">
                  <span :class="[primaryValue ? 'font-weight-bold' : fallback ? '' : 'disabled']">
                    {{ primaryValue || fallback || 'Benämning saknas' }}
                  </span>
                </template>
              </DisplayValueComponent>
            </label>
          </div>
        </div>

        <template v-if="querySkus && paginatedProducts && paginatedProducts?.results < querySkus?.total">
          <div v-if="querySkus" class="d-flex gap-1 align-items-center">
            <!-- <PaginationComponent v-if="baseProducts" v-model="page" :page-limit="pageLimit" :total="baseProducts.data.length" :limits="null" /> -->
            <button class="btn btn-light" :disabled="page === 0" @click="page--"><i class="fa fa-chevron-left" /></button>
            <button class="btn btn-light" :disabled="(page + 1) * pageLimit >= querySkus?.total" @click="page++"><i class="fa fa-chevron-right" /></button>
            {{ paginationText }}

            <label class="ml-4 user-select-none">
              <input v-model="togglePageSkusModel" :indeterminate="togglePageSkusIndeterminate" type="checkbox" />
              Välj alla artiklar på denna sida
            </label>
          </div>
        </template>
      </div>

      <div id="price-adjustment">
        <div class="position-sticky bg-white" style="top: 0">
          <div class="header">
            Prisssättning <em class="mx-1">{{ translate(defaultTargetBaseProperty?.defaultName, 'attributes') }}</em> (utgår från&nbsp;<em>
              {{ translate(defaultSourceProperty?.defaultName, 'attributes') }} </em
            >)
          </div>
        </div>

        <div class="content">
          <div v-for="(html, method) of priceAdjustmentMethods" :key="method" class="value">
            <input :id="method" v-model="adjustmentMethod" type="radio" :value="method" />
            <label :for="method" v-html="html" />
          </div>
        </div>

        <div v-if="adjustmentMethod !== 'remove'" class="position-relative mt-2">
          <input
            v-model="adjustmentAmount"
            type="number"
            min="0"
            :max="adjustmentMethod.includes('percentage') ? 100 : undefined"
            class="form-control no-counter"
          />
          <template v-if="adjustmentMethod.startsWith('percentage')"> % av {{ translate(defaultSourceProperty?.defaultName, 'attributes') }} </template>
          <template v-else> enheter </template>
        </div>
      </div>

      <div class="sticky-action-buttons d-flex gap-2 align-items-center flex-wrap" style="bottom: 0; padding: 0.5em 0">
        <!-- <button class="btn btn-primary" :disabled="selectedCountries.length === 0 || selectedSkus.size === 0 || amount === 0" @click="adjustPrice()">Ok</button> -->
        <ActionPendingButton variant="primary" :disabled="priceAdjustmentBlocked" :pending="calculationPending" @click="adjustPrice()">Ok</ActionPendingButton>
        <button class="btn btn-warning" @click="emit('close')">Stäng</button>
      </div>
    </div>
  </ModalComponent>
</template>

<script setup lang="ts">
import { useTranslation } from '@/composables/useTranslation'
import { BasicOption, Country } from '@/models/Global'
import { PriceAdjustmentMethod } from '@/models/Import'
import Message from '@/models/Message'
import { CountrySpecifics, ProductsResponse } from '@/models/Product'
import { StoredProperty } from '@/models/Property.js'
import DataService from '@/services/DataService'
import PropertyService from '@/services/PropertyService'
import { useDefaultStore } from '@/store'
import { useProductsStore } from '@/store/productsArchiveStore'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { usePropertyStore } from '@/store/propertyStore'
import { getAltLanguageField, getMainName } from '@/utils/productUtils'
import { getCountryFlag } from '@/utils/tools'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ActionPendingButton from '../ActionPendingButton.vue'
import DisplayValueComponent from '../DisplayValueComponent.vue'
import ModalComponent from '../ModalComponent.vue'
import MoreReadableSkuComponent from '../simple/MoreReadableSkuComponent.vue'

// -----------
// -- Setup --
// -----------
const store = useDefaultStore()
const bulkStore = useProductsBulkStore()
const productsStore = useProductsStore()
const propertyStore = usePropertyStore()
const route = useRoute()
const { appTranslate, translate } = useTranslation()

const props = defineProps<{ sourcePropertyPath: keyof CountrySpecifics; targetPropertyPath: keyof CountrySpecifics; activated?: boolean }>()
const emit = defineEmits(['close'])

const pageLimit = 50
let initiated = false

const priceAdjustmentMethods: Record<PriceAdjustmentMethod, string> = {
  percentage_decrease: ' Procentuell sänkning <strong> med </strong>...',
  percentage_to: 'Procentuell sänkning <strong> till </strong>... ',
  flat_decrease: `Minska med fast belopp...`,
  remove: 'Ta bort rabattpriser',
}

// -------------------
// -- Reactive vars --
// -------------------
const querySkus = ref<ProductsResponse<string>>()
const paginatedProducts = ref<ProductsResponse>()

const selectedCountries = ref<Country[]>([])
const selectedSkus = ref(new Set<string>())
const adjustmentAmount = ref(0)
const adjustmentMethod = ref<PriceAdjustmentMethod>('percentage_decrease')
const page = ref(0)
const calculationPending = ref(false)

// --------------------
// -- Computed props --
// --------------------

const defaultSourceProperty = computed(() => propertyStore.getPropertyByKey<StoredProperty<'countrySpecific'>>(props.sourcePropertyPath))
const defaultTargetBaseProperty = computed(() => propertyStore.getPropertyByKey<StoredProperty<'countrySpecific'>>(props.targetPropertyPath))

const allCountriesSelected = computed(() => selectedCountries.value.length === store.availableCountries.length)
const allProductsSelected = computed(() => selectedSkus.value.size === productsStore.currentTotalProducts)
const allPageProductsSelected = computed(() => !!paginatedProducts.value?.data.every((p) => selectedSkus.value.has(p.sku)))

const toggleAllIndeterminate = computed(() => selectedSkus.value.size > 0 && !allProductsSelected.value)
const togglePageSkusIndeterminate = computed(() => paginatedProducts.value?.data.some((p) => selectedSkus.value.has(p.sku)) && !allPageProductsSelected.value)

const toggleAllCountriesModel = computed({
  get: () => selectedCountries.value.length === store.availableCountries.length,
  set: () => {
    if (allCountriesSelected.value) {
      selectedCountries.value = []
    } else {
      selectedCountries.value = store.availableCountries
    }
  },
})

const toggleAllSkusModel = computed({
  get: () => selectedSkus.value.size === querySkus.value?.total,
  set: () => {
    if (!querySkus.value) {
      return
    }

    if (allProductsSelected.value) {
      selectedSkus.value.clear()
    } else {
      selectedSkus.value = new Set(querySkus.value.data)
    }
  },
})

const togglePageSkusModel = computed({
  get: () => allPageProductsSelected.value,
  set: () => {
    if (!paginatedProducts.value) {
      return
    }

    const operation = allPageProductsSelected.value ? 'delete' : 'add'

    for (const product of paginatedProducts.value.data) {
      selectedSkus.value[operation](product.sku)
    }
  },
})

const paginationText = computed(() => {
  if (querySkus.value) {
    const from = Math.min(page.value * pageLimit + 1, querySkus.value?.total)
    const to = Math.min(page.value * pageLimit + pageLimit, querySkus.value?.total)
    return `Visar ${from} - ${to} / ${querySkus.value?.total}`
  }
  return ''
})

const availableCountriesOptions = computed((): BasicOption<Country>[] =>
  store.availableCountries.map((country) => ({ value: country, displayValue: translate(country, 'countries', 'capitalize') })),
)

const priceAdjustmentBlocked = computed(
  () =>
    selectedCountries.value.length === 0 ||
    selectedSkus.value.size === 0 ||
    (adjustmentMethod.value.startsWith('percentage')
      ? adjustmentAmount.value <= 0 || adjustmentAmount.value >= 100
      : adjustmentMethod.value === 'flat_decrease'
        ? adjustmentAmount.value <= 0
        : adjustmentMethod.value !== 'remove'),
)

// ---------------------
// -- Lifecycle hooks --
// ---------------------

watch(
  () => page.value,
  async () => {
    if (props.activated) {
      updatePaginatedProducts()
    }
  },
)

watch(
  () => props.activated,
  async () => {
    if (props.activated && !initiated) {
      querySkus.value = await DataService.getProductsSkus({ ...route.query })
      updatePaginatedProducts()
      initiated = true
    }
  },
  {
    immediate: true,
  },
)

watch(
  () => route.fullPath,
  () => {
    selectedSkus.value.clear()
    initiated = false
  },
)

// ---------------
// -- Functions --
// ---------------

const updatePaginatedProducts = async () => {
  const from = page.value * pageLimit
  const to = from + pageLimit
  paginatedProducts.value = await DataService.getProducts({ ...route.query, from, to }, { includeCultureExclusiveData: true })
}

const adjustPrice = async () => {
  if (!selectedSkus.value || !defaultSourceProperty.value || !defaultTargetBaseProperty.value) {
    return
  }

  calculationPending.value = true

  try {
    const result = await DataService.getPriceAdjustments(
      Array.from(selectedSkus.value),
      bulkStore.productInputBySku,
      adjustmentAmount.value,
      adjustmentMethod.value,
      selectedCountries.value,
      props.sourcePropertyPath,
      props.targetPropertyPath,
    )

    for (const country of selectedCountries.value) {
      const targetProperty = PropertyService.getBasicProperty(defaultTargetBaseProperty.value, country && { country })

      for (const sku in result.productInputBySku) {
        const priceChangeInput = result.productInputBySku[sku]
        const newPropertyReturn = targetProperty.get(undefined, priceChangeInput)
        const newPrice = newPropertyReturn?.delete ? null : newPropertyReturn?.value

        if (newPrice === undefined || (typeof newPrice === 'object' && newPrice !== null)) {
          continue
        }

        let modified

        if (result.unchanged.sameAsCurrentPrice[sku]?.includes(country)) {
          targetProperty.reset(bulkStore.currentState[sku], undefined)
          modified = false
        } else {
          let productInput = bulkStore.currentState[sku]

          if (!bulkStore.currentState[sku]) {
            bulkStore.currentState[sku] = {}
            productInput = bulkStore.currentState[sku]
          }

          targetProperty.set(productInput, newPrice, undefined)
          modified = true
        }
        bulkStore.setModified(sku, targetProperty, modified)
        propertyStore.incPropertyResetKey(sku, targetProperty.key)
      }
    }

    bulkStore.pushHistoryEntry()

    const sameAsCurrentPriceEntries = Object.entries(result.unchanged.sameAsCurrentPrice)
    if (sameAsCurrentPriceEntries.length) {
      const sameAsCurrentPriceMsg = sameAsCurrentPriceEntries
        .map(([sku, countries]) => {
          const text = countries.map((country) => translate(country, 'countries', 'capitalize')).join(', ')
          return `<strong>${sku}:</strong> ${text}`
        })
        .join('\n')

      store.addMessage(new Message(`Återställde ${sameAsCurrentPriceEntries.length} artiklar i följande länder`, sameAsCurrentPriceMsg, 'success', 7))
    }

    const missingSourcePriceEntries = Object.entries(result.unchanged.missingSourcePrice)
    if (missingSourcePriceEntries.length) {
      const missingSourcePriceMsg = missingSourcePriceEntries
        .map(([sku, countries]) => {
          const text = countries.map((country) => translate(country, 'countries', 'capitalize')).join(', ')
          return `<strong>${sku}:</strong> ${text}`
        })
        .join('\n')

      store.addMessage(
        new Message(`Kunde inte sätta pris för ${missingSourcePriceEntries.length} artiklar i följande länder`, missingSourcePriceMsg, 'warning'),
      )
    }

    const errorEntries = Object.entries(result.unchanged.errored)
    if (errorEntries.length) {
      const errorMsg = errorEntries.map(([sku, countries]) => `<strong>${sku}:</strong> ${countries.join(', ')}`).join('\n')

      store.addMessage(new Message(`Kunde inte sätta pris för ${errorEntries.length} artiklar i följande länder`, errorMsg, 'warning'))
    }
    emit('close')
  } catch (err: any) {
    store.addMessage(new Message(appTranslate('An error occurred'), err.response?.data, 'danger'))
  } finally {
    calculationPending.value = false
  }
}
</script>

<style lang="scss" scoped>
.layout-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
  margin: 0.5em 1.5em;

  > * {
    width: 100%;
  }
}

.header,
label {
  padding: 3px 5px;
  margin: 0;
}

.header {
  background-color: #eee;
  margin-bottom: 0.5em;
}

input[type='checkbox'],
input[type='radio'] {
  $size: 1.1em;
  width: $size;
  min-width: $size;
  height: $size;
  min-height: $size;
  margin-right: 0.35em;
}

.header,
.value {
  display: flex;
  align-items: center;
}

.value {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  label {
    max-width: 100%;
    user-select: none;
    margin-bottom: 0;
  }
}

.content {
  padding: 0 0.25em;
}

.countries {
  columns: 1;

  @media (min-width: 500px) {
    columns: 2;
  }

  @media (min-width: 768px) {
    columns: 3;
  }

  @media (min-width: 992px) {
    columns: 4;
  }
}

.products {
  overflow-y: auto;
  margin-bottom: 0.5em;

  columns: 1;

  @media (min-width: 768px) {
    columns: 2;
  }

  @media (min-width: 998px) {
    columns: 3;
  }
}

.no-counter {
  width: 4em;
}
</style>
